/**
 * Die Umgebungskennzeichnung in der Oberfläche — Farben, Kennung, Ansage.
 *
 * Grundlage ist die Spec „Umgebungskennzeichnung" (me-design, docs/superpowers/specs/
 * 2026-09-07-umgebungskennzeichnung-design.md). Die Token-NAMEN unten sind wörtlich die
 * der Spec (`--umgebung-produktion`, `--umgebung-schrift`, `--kopfhoehe`, …), obwohl das
 * Design-System die Umsetzung selbst noch nicht hat: Sobald es sie bekommt, soll
 * design-sync diese Datei ablösen können, ohne dass irgendwo ein Name nachgezogen werden
 * muss.
 *
 * WARUM DIE KLASSENLISTE HIER EIN ZWEITES MAL STEHT: Dieselben drei Welten stehen in
 * @kundeninfo/domain (packages/domain/src/umgebung.ts), wo die Konfiguration ausgewertet
 * wird. `ui` darf `domain` nach der Abhängigkeitsrichtung nicht importieren (siehe
 * scripts/check-boundaries.mjs: `ui` hängt an nichts, und das ist gut so — es soll ohne
 * Server bestehen können). Was hier steht, ist deshalb bewusst nur das MINIMUM: die drei
 * Namen und die Regel „unbekannt heisst Test". Die eigentliche Regel — Debug-Widerspruch,
 * Umgebungsvariablen, Trimmen — bleibt drüben; hier wird nichts davon nachgebaut.
 * Zusammengehalten werden beide Listen von packages/web/test/umgebung.test.ts, das die
 * Liste hier gegen den erzeugten Vertragstyp des Servers legt.
 */
/** Die drei Welten. Weitere Klassen sind laut Spec nicht vorgesehen. */
export const UMGEBUNGSKLASSEN = ["produktion", "test", "entwicklung"];
/*
 * Die 3px-Akzentlinie unter der Kopfleiste stand bis ME-Etappe 1 hier (MARKENGRUEN, auf
 * der Testwelt MARKENGRUEN_AUF_BERNSTEIN). Seit Aufgabe 6 zeichnet sie das Element
 * me-kopfzeile selbst, in `--marke` des Arbeitsunternehmens; die Werte sind entfallen.
 */
export const UMGEBUNGSFARBEN = {
    produktion: {
        grund: "#004500",
        schrift: "#ffffff",
        sekundaer: "rgba(255, 255, 255, 0.72)",
        fokus: "#ACCA68",
    },
    test: {
        grund: "#F2A900",
        schrift: "#161617",
        sekundaer: "rgba(22, 22, 23, 0.72)",
        fokus: "#161617",
    },
    entwicklung: {
        grund: "#3F2A6E",
        schrift: "#ffffff",
        sekundaer: "rgba(255, 255, 255, 0.72)",
        fokus: "#ACCA68",
    },
};
/** Die feste Höhe der Hauptzeile (`--kopfhoehe`). Fest, nicht Mindesthöhe. */
export const KOPFHOEHE_PX = 52;
/**
 * Aus irgendeinem Wert eine Welt machen — AUSFALLSICHER in dieselbe Richtung wie
 * serverseitig: Was nicht ausdrücklich Produktion oder Entwicklung ist, ist Test. Das
 * gilt für `undefined` (die Sitzung steht noch nicht), für einen Tippfehler und für einen
 * Wert aus einer älteren Serverfassung, die diese Auskunft noch nicht kannte.
 */
export function normalisiereUmgebung(wert) {
    return wert === "produktion" || wert === "entwicklung" ? wert : "test";
}
/**
 * Die Kennung einer Welt — `null` bei Produktion, denn Produktion ist der STILLE Zustand
 * (Spec: kein Abzeichen, nur die Farbe).
 *
 * Beim Konfigurationskonflikt steht anstelle des Instanznamens das Wort
 * „Konfigurationskonflikt": Wer diese Kopfleiste sieht, soll nicht rätseln, warum ein
 * System, das produktiv sein sollte, gelb ist. Die Spec schreibt den Satz als
 * „Test · Konfigurationskonflikt"; hier steht das Weltwort wie überall sonst in Versalien
 * („TEST · …"), weil ein einzelnes anders geschriebenes Abzeichen wie eine ANDERE Art
 * Abzeichen aussähe.
 */
export function umgebungsKennung(anzeige) {
    if (anzeige.klasse === "produktion")
        return null;
    if (anzeige.konflikt === true) {
        return { welt: "TEST", weltKurz: "TEST", zusatz: "Konfigurationskonflikt" };
    }
    const worte = anzeige.klasse === "test"
        ? { welt: "TEST", weltKurz: "TEST" }
        : { welt: "ENTWICKLUNG", weltKurz: "ENTW" };
    return { ...worte, zusatz: anzeige.instanz };
}
/**
 * Die Ansage am Banner (`aria-label`) — IMMER vorhanden, auch in Produktion, und immer
 * vollständig: Farbe allein darf nie das einzige Merkmal sein (Spec), und ein
 * Screenreader liest keine Kopfleistenfarbe.
 */
export function umgebungsAnsage(anzeige) {
    if (anzeige.konflikt === true)
        return "Testsystem, Konfigurationskonflikt";
    const wort = anzeige.klasse === "produktion"
        ? "Produktivsystem"
        : anzeige.klasse === "test" ? "Testsystem" : "Entwicklungssystem";
    // In Produktion bleibt der Instanzname weg — dort steht auch sichtbar keine Kennung,
    // und die Ansage soll dasselbe sagen wie das Bild.
    if (anzeige.klasse === "produktion" || anzeige.instanz === null)
        return wort;
    return `${wort} ${anzeige.instanz}`;
}
//# sourceMappingURL=umgebung.js.map